import { DataTable } from "@primereact/ui/datatable";
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import type { AIUsageStatsResponse } from "@offertli/shared";
import { DatePicker as Calendar } from "@primereact/ui/datepicker";
import { InputText as Input } from "@primereact/ui/inputtext";
import { Field } from "../../components/ui/Field.js";
import { apiGet } from "../../lib/api.js";
import { ErrorPanel, LoadingPanel, Section } from "../shared.js";
function currentMonth(): string {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
}
function money(value: string): string {
  return new Intl.NumberFormat(undefined, {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 4,
  }).format(Number(value));
}
export function AIUsagePanel() {
  const { t } = useTranslation();
  const [month, setMonth] = useState(currentMonth);
  const usage = useQuery({
    queryKey: ["ai", "usage", month],
    queryFn: () =>
      apiGet<{
        data: AIUsageStatsResponse;
      }>("/ai/usage", {
        query: {
          month,
        },
      }).then((response) => response.data),
  });
  if (usage.isLoading) return <LoadingPanel />;
  if (usage.error || !usage.data) return <ErrorPanel error={usage.error} />;
  const data = usage.data;
  return (
    <div className="space-y-5">
      <Section
        title={t("settings.ai.title", {
          defaultValue: "AI usage and budget",
        })}
      >
        <div className="mb-5 max-w-xs">
          <Field
            label={t("settings.ai.month", {
              defaultValue: "Billing month",
            })}
          >
            <Calendar.Root
              view="month"
              value={month ? new Date(`${month}-01T12:00:00`) : null}
              maxDate={new Date(`${currentMonth()}-01T12:00:00`)}
              onValueChange={(event: { value: unknown }) => {
                if (event.value instanceof Date) {
                  setMonth(
                    `${event.value.getFullYear()}-${String(event.value.getMonth() + 1).padStart(2, "0")}`,
                  );
                }
              }}
              dateFormat="yy-mm"
              showIcon
              fluid
            >
              <Calendar.Input as={Input} />
              <Calendar.Trigger className="pi pi-calendar" type="button" />
              <Calendar.Portal>
                <Calendar.Positioner>
                  <Calendar.Popup>
                    <Calendar.Calendar>
                      <Calendar.Header>
                        <Calendar.Prev className="pi pi-chevron-left" />
                        <Calendar.Title />
                        <Calendar.Next className="pi pi-chevron-right" />
                      </Calendar.Header>
                      <Calendar.Body>
                        <Calendar.Table>
                          <Calendar.TableHead />
                          <Calendar.TableBody view="date" />
                          <Calendar.TableBody view="month" />
                          <Calendar.TableBody view="year" />
                        </Calendar.Table>
                      </Calendar.Body>
                    </Calendar.Calendar>
                  </Calendar.Popup>
                </Calendar.Positioner>
              </Calendar.Portal>
            </Calendar.Root>
          </Field>
        </div>
        {!data.configured ? (
          <p
            role="status"
            className="rounded-lg bg-warning-soft p-3 text-sm text-warning"
          >
            {t("settings.ai.notConfigured", {
              defaultValue:
                "AI is unavailable. Configure OPENROUTER_API_KEY on the server to enable it.",
            })}
          </p>
        ) : null}
        <dl className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <div>
            <dt className="text-xs uppercase text-app-muted">
              {t("settings.ai.model", {
                defaultValue: "Model",
              })}
            </dt>
            <dd className="break-all font-medium text-app-text">
              {data.model}
            </dd>
          </div>
          <div>
            <dt className="text-xs uppercase text-app-muted">
              {t("settings.ai.budget", {
                defaultValue: "Monthly budget",
              })}
            </dt>
            <dd className="font-medium text-app-text">
              {money(data.monthlyBudgetUsd)}
            </dd>
          </div>
          <div>
            <dt className="text-xs uppercase text-app-muted">
              {t("settings.ai.used", {
                defaultValue: "Used",
              })}
            </dt>
            <dd className="font-medium text-app-text">{money(data.usedUsd)}</dd>
          </div>
          <div>
            <dt className="text-xs uppercase text-app-muted">
              {t("settings.ai.remaining", {
                defaultValue: "Remaining",
              })}
            </dt>
            <dd className="font-medium text-app-text">
              {money(data.remainingUsd)}
            </dd>
          </div>
        </dl>
      </Section>
      <Section
        title={t("settings.ai.byUser", {
          defaultValue: "Usage by user",
        })}
      >
        <DataTable.Root>
          <DataTable.TableContainer>
            <DataTable.Table>
              <DataTable.THead>
                <DataTable.Row>
                  <DataTable.THeadCell>
                    <DataTable.THeadTitle>
                      {t("settings.ai.user", {
                        defaultValue: "User",
                      })}
                    </DataTable.THeadTitle>
                  </DataTable.THeadCell>
                  <DataTable.THeadCell>
                    <DataTable.THeadTitle>
                      {t("settings.ai.requests", {
                        defaultValue: "Requests",
                      })}
                    </DataTable.THeadTitle>
                  </DataTable.THeadCell>
                  <DataTable.THeadCell>
                    <DataTable.THeadTitle>
                      {t("settings.ai.tokens", {
                        defaultValue: "Tokens",
                      })}
                    </DataTable.THeadTitle>
                  </DataTable.THeadCell>
                  <DataTable.THeadCell>
                    <DataTable.THeadTitle>
                      {t("settings.ai.cost", {
                        defaultValue: "Cost",
                      })}
                    </DataTable.THeadTitle>
                  </DataTable.THeadCell>
                </DataTable.Row>
              </DataTable.THead>
              <DataTable.TBody className="divide-y divide-app-border">
                {data.users.map((row) => (
                  <DataTable.Row key={row.userId}>
                    <DataTable.Cell>{row.displayName}</DataTable.Cell>
                    <DataTable.Cell>{row.requestCount}</DataTable.Cell>
                    <DataTable.Cell>
                      {row.totalTokens.toLocaleString()}
                    </DataTable.Cell>
                    <DataTable.Cell>{money(row.costUsd)}</DataTable.Cell>
                  </DataTable.Row>
                ))}
                {!data.users.length ? (
                  <DataTable.Row>
                    <DataTable.Cell
                      colSpan={4}
                      className="py-10 text-center text-app-muted"
                    >
                      {t("settings.ai.empty", {
                        defaultValue: "No AI requests were made in this month.",
                      })}
                    </DataTable.Cell>
                  </DataTable.Row>
                ) : null}
              </DataTable.TBody>
            </DataTable.Table>
          </DataTable.TableContainer>
        </DataTable.Root>
      </Section>
    </div>
  );
}
